import React, { useState } from "react";
import { useTrending } from "../../hooks/useTrending";

const PUBS = [
  { key: "all",        label: "All",                color: "#64748B" },
  { key: "newsday",    label: "NewsDay",             color: "#4DA3FF" },
  { key: "zimind",     label: "Zim Independent",     color: "#B07CFF" },
  { key: "standard",  label: "The Standard",         color: "#2DD4BF" },
  { key: "southerneye", label: "Southern Eye",       color: "#FFC857" },
];

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 60000;
  if (diff < 60) return `${Math.round(diff)}m ago`;
  if (diff < 1440) return `${Math.round(diff / 60)}h ago`;
  return `${Math.round(diff / 1440)}d ago`;
}

const RANK_COLORS = ["#D85A30", "#BA7517", "#185FA5"];
const BAR_NOISE = [0.60, 0.88, 0.72, 1.00, 0.78, 0.82, 0.92];

function StoryCard({ item, rank, color, t }) {
  const [hovered, setHovered] = useState(false);
  const rankColor = RANK_COLORS[rank] || "#888";
  const category = item.category || item.topic || item.pubLabel || "News";
  const estViews = item.views || Math.round(9000 / Math.max(0.5, rank * 0.9 + 0.4));

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex", gap: 14, padding: "14px 16px",
        background: hovered ? `${color}0d` : "transparent",
        borderBottom: `1px solid ${t.border}`,
        transition: "background 0.15s",
        cursor: "default",
      }}>
      {/* Large rank number */}
      <div style={{
        fontSize: 30, fontWeight: 900, color: rankColor, lineHeight: 1,
        minWidth: 36, fontFamily: "Georgia, serif", flexShrink: 0,
        opacity: rank < 3 ? 1 : 0.55, paddingTop: 2,
      }}>
        {rank + 1}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Category pill */}
        <div style={{ marginBottom: 5 }}>
          <span style={{
            fontSize: 9, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.8,
            background: `${color}18`, color, borderRadius: 4, padding: "2px 7px",
          }}>
            {category}
          </span>
        </div>

        {/* Title */}
        <a href={item.url || "#"} target="_blank" rel="noopener noreferrer"
          style={{ fontSize: 13, fontWeight: 700, color: t.text, textDecoration: "none", fontFamily: "Georgia, serif", lineHeight: 1.4, display: "block", marginBottom: 6 }}>
          {item.aiTitle || item.title}
        </a>

        {/* Bottom meta row */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 9, color: t.textMuted, fontWeight: 700 }}>
            {item.pubLabel || item.publication}
          </span>
          {item.publishedAt && (
            <span style={{ fontSize: 9, color: t.textFaint }}>{timeAgo(item.publishedAt)}</span>
          )}
          {item.author && (
            <span style={{ fontSize: 9, color: t.textFaint }}>by {item.author}</span>
          )}
          {/* Eye icon + estimated views */}
          <span style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 9, color: t.textMuted, marginLeft: "auto" }}>
            <svg width={11} height={8} viewBox="0 0 11 8" fill="none">
              <path d="M5.5 1C3 1 1 4 1 4s2 3 4.5 3 4.5-3 4.5-3-2-3-4.5-3z" stroke={t.textFaint} strokeWidth="1.2"/>
              <circle cx="5.5" cy="4" r="1.2" fill={t.textFaint}/>
            </svg>
            {estViews.toLocaleString()}
          </span>
          {/* Mini bar sparkline */}
          <svg width={44} height={16} viewBox="0 0 44 16" style={{ flexShrink: 0 }}>
            {BAR_NOISE.map((n, i) => {
              const h = Math.max(2, Math.round(n * 12));
              return <rect key={i} x={i * 6 + 1} y={16 - h} width={4} height={h} rx={1} fill={rankColor} opacity={0.45 + i * 0.08} />;
            })}
          </svg>
        </div>
      </div>
    </div>
  );
}


export function TrendingPanel({ isMobile, theme: t }) {
  const { items, loading, error, aiEnhanced, updatedAt, refresh } = useTrending();
  const [activePub, setActivePub] = useState("all");

  const activePubInfo = PUBS.find(p => p.key === activePub);
  const filteredItems = activePub === "all" ? items : items.filter(i => i.publication === activePub);

  if (loading) {
    return (
      <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 20, padding: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
          <span style={{ fontSize: 16 }}>🔥</span>
          <h3 style={{ margin: 0, fontSize: 14, color: t.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>Top Stories</h3>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, color: t.textFaint, fontSize: 13 }}>
          <div style={{ width: 16, height: 16, border: `2px solid ${t.border}`, borderTopColor: "#2563EB", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
          Loading stories…
        </div>
      </div>
    );
  }

  if (error && items.length === 0) {
    return (
      <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 20, padding: 24 }}>
        <h3 style={{ margin: "0 0 12px", fontSize: 14, color: t.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>🔥 Top Stories</h3>
        <div style={{ fontSize: 13, color: t.textFaint, fontStyle: "italic" }}>RSS feeds not reachable — check server connection.</div>
      </div>
    );
  }

  return (
    <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 20, padding: isMobile ? 16 : 24 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 16 }}>🔥</span>
          <h3 style={{ margin: 0, fontSize: 14, color: t.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>Top Stories</h3>
          {aiEnhanced && (
            <span style={{ fontSize: 9, background: "#7C3AED22", border: "1px solid #7C3AED44", color: "#7C3AED", borderRadius: 6, padding: "2px 7px", fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.8 }}>AI</span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {updatedAt && <span style={{ fontSize: 10, color: t.textFaint }}>{timeAgo(updatedAt)}</span>}
          <button onClick={refresh}
            style={{ fontSize: 10, color: t.textMuted, background: "none", border: "none", cursor: "pointer", padding: "4px 8px", borderRadius: 6, textTransform: "uppercase", letterSpacing: 0.6 }}
            onMouseEnter={e => e.currentTarget.style.background = t.surface2}
            onMouseLeave={e => e.currentTarget.style.background = "none"}>
            ↺ Refresh
          </button>
        </div>
      </div>

      {/* Platform tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
        {PUBS.map(pub => {
          const isActive = activePub === pub.key;
          const count = pub.key === "all" ? Math.min(items.length, 10) : items.filter(i => i.publication === pub.key).length;
          return (
            <button key={pub.key} onClick={() => setActivePub(pub.key)}
              style={{
                padding: "5px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700, cursor: "pointer",
                border: isActive ? `1.5px solid ${pub.color}` : `1px solid ${t.border}`,
                background: isActive ? `${pub.color}18` : "transparent",
                color: isActive ? pub.color : t.textFaint,
                transition: "all 0.15s",
                display: "flex", alignItems: "center", gap: 5,
              }}>
              {pub.label}
              {count > 0 && (
                <span style={{ fontSize: 9, background: isActive ? pub.color : t.border, color: isActive ? "#fff" : t.textFaint, borderRadius: 10, padding: "1px 5px", fontWeight: 900 }}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Content */}
      {activePub === "all" ? (
        /* Flat top-10 list across all publications */
        <div style={{ display: "flex", flexDirection: "column" }}>
          {items.length === 0 ? (
            <div style={{ fontSize: 13, color: t.textFaint, fontStyle: "italic", padding: "16px 0", textAlign: "center" }}>
              No stories available.
            </div>
          ) : (
            items.slice(0, 10).map((item, i) => {
              const pub = PUBS.find(p => p.key === item.publication) || PUBS[0];
              return <StoryCard key={item.id || i} item={item} rank={i} color={pub.color} t={t} />;
            })
          )}
        </div>
      ) : (
        /* Single platform stories */
        <div style={{ display: "flex", flexDirection: "column" }}>
          {filteredItems.length === 0 ? (
            <div style={{ fontSize: 13, color: t.textFaint, fontStyle: "italic", padding: "16px 0", textAlign: "center" }}>
              No stories available for {activePubInfo?.label}.
            </div>
          ) : (
            filteredItems.slice(0, 10).map((item, i) => (
              <StoryCard key={item.id || i} item={item} rank={i} color={activePubInfo?.color || "#2563EB"} t={t} />
            ))
          )}
        </div>
      )}
    </div>
  );
}
